import { Button, SelectButton } from '@bennows/boows-react';
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatNumber } from '../../lib/format';

export interface ChartSeries {
  readonly key: string;
  readonly label: string;
}

export interface ChartCategory {
  readonly key: string;
  readonly label: string;
}

export type StatChartKind = 'bar' | 'stacked' | 'line';

export interface StatChartProps {
  readonly title: string;
  readonly kind: StatChartKind;
  /** The x axis in order: days, weeks, months or groups. */
  readonly categories: readonly ChartCategory[];
  /** At most eight series; each takes the next `--chart-n` colour. */
  readonly series: readonly ChartSeries[];
  /** null: no value (a gap, never drawn as 0). */
  readonly value: (category: string, series: string) => number | null;
  readonly format?: (value: number) => string;
  /** The table view's caption; it names what the numbers are. */
  readonly tableCaption: string;
  readonly height?: number;
}

const VIEWS = ['chart', 'table'] as const;
type View = (typeof VIEWS)[number];

/** `--chart-1` .. `--chart-8` (tokens.css): the colour of the n-th series. */
const colourOf = (index: number) => `var(--chart-${(index % 8) + 1})`;

/**
 * One interactive chart (ADR-031, style guide 2.95, REQ-120-014): bars,
 * stacked bars or lines over the categories, a tooltip on hover, tap and
 * keyboard focus (Recharts' accessibility layer: Tab into the chart, arrow
 * keys walk the points), a legend whose buttons hide and show series, and a
 * table view with the same numbers. The legend and table name every series,
 * so colour is never the only signal.
 */
export function StatChart({ title, kind, categories, series, value, format = (number) => formatNumber(number), tableCaption, height = 260 }: StatChartProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const [view, setView] = useState<View>('chart');
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const rows = useMemo(
    () => categories.map((category) => ({ name: category.label, ...Object.fromEntries(series.map((item) => [item.key, value(category.key, item.key)])) })),
    [categories, series, value],
  );
  const toggle = (key: string) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else if (series.length - next.size > 1) next.add(key);
      return next;
    });
  const visible = series.map((item, index) => ({ ...item, colour: colourOf(index) })).filter((item) => !hidden.has(item.key));
  const tooltipValue = (raw: unknown) => (typeof raw === 'number' ? format(raw) : t('charts.noValue'));

  const chart = () => {
    const axes = (
      <>
        <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={12} />
        <YAxis tickLine={false} axisLine={false} width={48} allowDecimals={false} tickFormatter={(tick: number) => format(tick)} />
        <Tooltip formatter={tooltipValue} cursor={{ fill: 'var(--color-surface-alt)' }} />
      </>
    );
    if (kind === 'line') {
      return (
        <LineChart data={rows} accessibilityLayer>
          {axes}
          {visible.map((item) => (
            <Line key={item.key} type="monotone" dataKey={item.key} name={item.label} stroke={item.colour} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
          ))}
        </LineChart>
      );
    }
    return (
      <BarChart data={rows} accessibilityLayer>
        {axes}
        {visible.map((item) => (
          <Bar key={item.key} dataKey={item.key} name={item.label} fill={item.colour} stackId={kind === 'stacked' ? 'stack' : undefined} maxBarSize={40} isAnimationActive={false} />
        ))}
      </BarChart>
    );
  };

  return (
    <figure className="ui-card stat-chart" aria-labelledby={titleId}>
      <div className="stat-chart__head">
        <figcaption id={titleId} className="stat-chart__title">
          {title}
        </figcaption>
        <SelectButton
          size="sm"
          hideLabel
          label={t('charts.view.label')}
          value={view}
          options={VIEWS.map((id) => ({ id, label: t(`charts.view.${id}`) }))}
          onChange={(id) => setView(id as View)}
        />
      </div>
      {series.length > 1 ? (
        <div className="stat-chart__legend" role="group" aria-label={t('charts.legend')}>
          {series.map((item, index) => (
            <Button
              key={item.key}
              variant="ghost"
              size="sm"
              aria-pressed={!hidden.has(item.key)}
              className="stat-chart__legend-item"
              iconPrefix={<span className={`stat-chart__swatch stat-chart__swatch--${(index % 8) + 1}`} aria-hidden="true" />}
              onClick={() => toggle(item.key)}
            >
              {item.label}
            </Button>
          ))}
        </div>
      ) : null}
      {view === 'chart' ? (
        <div className="stat-chart__plot">
          <ResponsiveContainer width="100%" height={height}>
            {chart()}
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="stat-chart__table" role="region" aria-label={tableCaption} tabIndex={0}>
          <table className="ui-table">
            <caption className="ui-visually-hidden">{tableCaption}</caption>
            <thead>
              <tr>
                <th scope="col">{t('charts.category')}</th>
                {series.map((item) => (
                  <th key={item.key} scope="col">
                    {item.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.key}>
                  <th scope="row">{category.label}</th>
                  {series.map((item) => {
                    const number = value(category.key, item.key);
                    return <td key={item.key}>{number === null ? t('charts.noValue') : format(number)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </figure>
  );
}
